import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Helper function to get CSRF token from cookies
function getCookie(name: string): string | null {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// Configure axios defaults
const api = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,             // send cookies (sessionid, csrftoken)
  xsrfCookieName: 'csrftoken',       // cookie name Django sets
  xsrfHeaderName: 'X-CSRFToken',     // header Django expects
});

// Enable axios automatic CSRF token handling
axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFToken';

// ------------------
// Interceptor: attach CSRF token to every request (if not already present)
// ------------------
api.interceptors.request.use((config) => {
  // Always try to get the latest CSRF token
  const csrfToken = getCookie('csrftoken');
  
  // Set token if available (override any existing token)
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  
  console.log('🔐 Request interceptor:', {
    url: config.url,
    method: config.method?.toUpperCase(),
    hasCSRFToken: !!csrfToken,
    csrfToken: csrfToken ? `${csrfToken.substring(0, 10)}...` : 'null',
    headers: config.headers
  });
  
  return config;
});

// Response interceptor to handle authentication errors and CSRF issues
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401) {
      // Redirect to login if not authenticated
      console.warn('401 Unauthorized - redirecting to login');
      window.location.href = `${API_BASE_URL}/accounts/google/login/`;
    } else if (error.response?.status === 403 && !originalRequest._retry) {
      // Check if this is an admin-only endpoint that should not trigger redirects
      const isAdminOnlyEndpoint = originalRequest.url?.includes('/stats/team/') ||
                                  originalRequest.url?.includes('/admin/') ||
                                  originalRequest.url?.includes('leads/import/');
      
      if (isAdminOnlyEndpoint) {
        // Don't redirect for admin-only endpoints, just log and continue
        console.warn('403 Forbidden - Admin permission required for:', originalRequest.url);
      } else {
        // CSRF token might be expired, try to refresh it once for other 403 errors
        console.warn('403 Forbidden - attempting to refresh CSRF token');
        originalRequest._retry = true;
        
        try {
          const newToken = await ensureCSRFToken();
          if (newToken) {
            originalRequest.headers['X-CSRFToken'] = newToken;
            console.log('🔄 Retrying request with new CSRF token:', newToken);
            return api(originalRequest);
          }
        } catch (refreshError) {
          console.error('Failed to refresh CSRF token:', refreshError);
        }
      }
    }
    
    // Log all API errors for debugging
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    
    return Promise.reject(error);
  }
);

export interface User {
  id: number;
  email: string;
  name: string;
  is_admin: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  is_active: boolean;
  daily_leads_completed: number;
  last_lead_date: string | null;
  date_joined: string;
}

export interface QualificationDetails {
  // New format with human-readable reasons
  qualification_reasons?: string[];
  qualification_data?: {
    total_revenue: number;
    yearly_revenue: number;
    states: string[];
    state_count: number;
    gem_percentage?: number | null;
    railway_percentage?: number | null;
    total_bids: number;
    qualification_date: string;
  };
  crm_status?: {
    found: boolean;
    crm_id?: number;
    crm_link?: string;
    company_key?: string;
    paying_customer?: boolean;
    assigned_to?: string;
    email?: string;
    stage?: string;
    sales_stage?: string;
    date_joined?: string;
    reason?: string;
  };
  
  // Legacy formats
  qualification_filters?: {
    [key: string]: string;
  };
  company_profile?: {
    industry: string;
    total_bids: number;
    total_wins: number;
    win_rate: string;
    bidding_since: string;
    organizations_worked_with: number;
    competitor_count: number;
    platforms: string[];
  };
  qualification_score?: {
    total_score: number;
    revenue_score: number;
    activity_score: number;
    diversity_score: number;
    win_rate_score: number;
    platform_score: number;
  };
  // Legacy support for old format
  parameters_checked?: {
    [key: string]: {
      value: any;
      threshold?: number;
      passed: boolean;
      description: string;
    };
  };
  crm_match?: {
    found: boolean;
    crm_company_key: string | null;
    description: string;
  };
}

export interface Lead {
  id: number;
  company_name: string;
  palladium_link: string | null;
  crm_link: string | null;
  lead_score: number;
  qualification_status: string;
  source_type: string;
  is_crm_sourced: boolean;
  assigned_to: string | null;
  assigned_at: string | null;
  locked_until: string | null;
  leadgen_status: string | null;
  completed_at: string | null;
  not_qualified_reason: string | null;
  good_emails_count: number;
  generic_emails_count: number;
  phone_numbers_count: number;
  generic_phones_count: number;
  primary_source: string | null;
  is_verified: boolean;
  metadata: any;
  created_at: string;
  updated_at: string;
  time_locked_remaining: number;
  is_lock_expired: boolean;
  assigned_user_name: string | null;
  // New CRM integration fields
  crm_company_key: string | null;
  qualification_details: string | null;
  qualification_details_parsed: QualificationDetails | null;
  // Time tracking fields
  time_remaining_seconds?: number;
  time_elapsed_seconds?: number;
}

export interface UserStats {
  user_info: {
    name: string;
    email: string;
    is_admin: boolean;
    daily_leads_completed: number;
    last_lead_date: string | null;
  };
  timeline_stats: {
    total_events: number;
    recent_events: any[];
  };
  leads_stats: {
    pending_leads: number;
  };
  current_assignment: Lead | null;
}

export interface LeadCompletion {
  status: 'successful' | 'unsuccessful';
  good_emails_count: number;
  generic_emails_count: number;
  phone_numbers_count: number;
  primary_source: string;
  is_verified: boolean;
  not_qualified_reason?: string;
}

export interface CrmLeadUpdateResponse {
  message: string;
  crm_link: string;
  crm_id: string;
  appointment: string;
  updated_fields: {
    cc_email: string;
    sprint_number: string;
    appointment: string;
  };
}

export type LeadImportClassification =
  | 'will_import'
  | 'duplicate'
  | 'crm_owned'
  | 'invalid';

export interface LeadImportSummary {
  total: number;
  will_import: number;
  duplicate: number;
  crm_owned: number;
  invalid: number;
}

export interface LeadImportPreviewRow {
  row_number: number;
  company_name: string;
  classification: LeadImportClassification;
  reason: string;
}

export interface LeadImportPreview {
  import_id: number;
  file_name: string;
  summary: LeadImportSummary;
  rows: LeadImportPreviewRow[];
}

export interface LeadImportConfirmation {
  imported: number;
  skipped_late_duplicates: number;
  skipped_late_crm_owned: number;
  summary: LeadImportSummary;
  rows: LeadImportPreviewRow[];
}

// Helper function to ensure CSRF token is available
async function ensureCSRFToken(): Promise<string | null> {
  let existingToken = getCookie('csrftoken');
  console.log('🔍 Checking existing CSRF token:', existingToken);

  if (!existingToken) {
    try {
      console.log('🔄 Fetching new CSRF token...');
      
      // Try the dedicated CSRF endpoint first
      console.log('📡 Calling /api/csrf/ endpoint...');
      await axios.get(`${API_BASE_URL}/api/csrf/`, { 
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      existingToken = getCookie('csrftoken');
      console.log('🔑 CSRF token after /api/csrf/:', existingToken);
      
      // If still no token, try status endpoint
      if (!existingToken) {
        console.log('📡 Calling /api/v1/status/ endpoint...');
        await axios.get(`${API_BASE_URL}/api/v1/status/`, { 
          withCredentials: true,
          headers: {
            'Accept': 'application/json'
          }
        });
        existingToken = getCookie('csrftoken');
        console.log('🔑 CSRF token after status:', existingToken);
      }
      
    } catch (error) {
      console.error('❌ Failed to get CSRF token:', error);
      return null;
    }
  }
  
  console.log('🎯 Final CSRF token to use:', existingToken);
  return existingToken;
}

export const apiService = {
  // Authentication
  async getCurrentUser(): Promise<User> {
    await ensureCSRFToken();
    const response = await api.get('users/me/');
    return response.data;
  },

  async logout(): Promise<{ success: boolean; message: string }> {
    let csrfToken = getCookie('csrftoken');

    if (!csrfToken) {
      const statusResp = await axios.get(`${API_BASE_URL}/api/v1/status/`, { withCredentials: true });
      csrfToken = getCookie('csrftoken') || statusResp.headers['x-csrftoken'] || '';
    }

    const response = await api.post(
      'auth/logout/',
      {},
      {
        headers: { 'X-CSRFToken': csrfToken },
      }
    );
    return response.data;
  },

  async getUserStats(): Promise<UserStats> {
    const response = await api.get('users/stats/');
    return response.data;
  },

  // Leads
  async getLeads(page = 1): Promise<{ results: Lead[]; count: number; next: string | null; previous: string | null }> {
    const response = await api.get(`leads/?page=${page}`);
    return response.data;
  },

  async getLead(id: number): Promise<Lead> {
    const response = await api.get(`leads/${id}/`);
    return response.data;
  },

  async validateLeadImport(file: File, batchNotes: string): Promise<LeadImportPreview> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('batch_notes', batchNotes);
    const response = await api.post('leads/import/validate/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async confirmLeadImport(importId: number): Promise<LeadImportConfirmation> {
    const response = await api.post(`leads/import/${importId}/confirm/`, {});
    return response.data;
  },

  async downloadLeadImportTemplate(): Promise<Blob> {
    const response = await api.get('leads/import/template/', {
      responseType: 'blob',
    });
    return response.data;
  },

  async getNextLead(): Promise<{ success: boolean; lead?: Lead; message: string }> {
    const response = await api.get('leads/next/');
    return response.data;
  },

  async getCurrentLead(): Promise<Lead | null> {
    try {
      const response = await api.get('leads/current/');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  async completeLead(id: number, completion: LeadCompletion): Promise<{ success: boolean; lead: Lead; message: string }> {
    console.log('🚀 API: Starting completeLead for lead:', id, 'with data:', completion);
    
    try {
      const token = await ensureCSRFToken();
      console.log('🔐 CSRF token for completeLead:', token);
      
      // Proceed even if token is null - the interceptor might handle it
      const headers: any = {};
      if (token) {
        headers['X-CSRFToken'] = token;
      }
      
      console.log('📤 Making POST request to leads/' + id + '/complete/');
      const response = await api.post(`leads/${id}/complete/`, completion, {
        headers
      });
      
      console.log('✅ API: completeLead response:', response.data);
      return response.data;
      
    } catch (error: any) {
      console.error('❌ API: completeLead failed:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  },

  async releaseLead(id: number, releaseData?: { reason: string; description: string }): Promise<{ success: boolean; message: string }> {
    console.log('🚀 API: Starting releaseLead for lead:', id, 'with data:', releaseData);
    
    try {
      const token = await ensureCSRFToken();
      console.log('🔐 CSRF token for releaseLead:', token);
      
      // Proceed even if token is null - the interceptor might handle it
      const headers: any = {};
      if (token) {
        headers['X-CSRFToken'] = token;
      }
      
      console.log('📤 Making POST request to leads/' + id + '/release/');
      const response = await api.post(`leads/${id}/release/`, releaseData, {
        headers
      });
      
      console.log('✅ API: releaseLead response:', response.data);
      return response.data;
      
    } catch (error: any) {
      console.error('❌ API: releaseLead failed:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  },

  async createCrmProfile(id: number, emailId: string): Promise<{ message: string; crm_link: string; crm_id: number }> {
    const token = await ensureCSRFToken();
    const headers: any = {};
    if (token) {
      headers['X-CSRFToken'] = token;
    }
    const response = await api.post(`leads/${id}/create-crm-profile/`, { email_id: emailId }, { headers });
    return response.data;
  },

  async updateCrmLead(id: number, updateData: { cc_email: string; sprint_number: string }): Promise<CrmLeadUpdateResponse> {
    const token = await ensureCSRFToken();
    const headers: any = {};
    if (token) {
      headers['X-CSRFToken'] = token;
    }
    const response = await api.post(`leads/${id}/update-crm/`, updateData, { headers });
    return response.data;
  },

  // Timeline
  async getTimeline(page = 1, userEmail?: string, startDate?: string, endDate?: string, status?: string, search?: string): Promise<{ results: any[]; count: number; next: string | null; previous: string | null }> {
    let url = `timeline/?page=${page}`;
    if (userEmail && userEmail !== 'all') {
      url += `&user_email=${encodeURIComponent(userEmail)}`;
    }
    if (startDate) {
      url += `&start_date=${encodeURIComponent(startDate)}`;
    }
    if (endDate) {
      url += `&end_date=${encodeURIComponent(endDate)}`;
    }
    if (status && status !== 'all') {
      url += `&status=${encodeURIComponent(status)}`;
    }
    if (search && search.trim()) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    const response = await api.get(url);
    return response.data;
  },

  // Stats
  async getPersonalStats(period: string = 'today'): Promise<{ leads_completed: number; success_rate: number; average_time_minutes: number; by_source: any }> {
    const response = await api.get(`stats/personal/?period=${period}`);
    return response.data;
  },

  async getTeamStats(period: string = 'today'): Promise<any> {
    const response = await api.get(`stats/team/?period=${period}`);
    return response.data;
  },

  // Health check
  async getStatus(): Promise<{ status: string; message: string; authenticated: boolean }> {
    const response = await axios.get(`${API_BASE_URL}/api/v1/status/`, { withCredentials: true });
    return response.data;
  },

  // Generic GET method
  async get(url: string): Promise<any> {
    const response = await api.get(url);
    return response;
  },

  // Generic POST method
  async post(url: string, data: any): Promise<any> {
    const response = await api.post(url, data);
    return response;
  },

  // Marketing functions
  async getMarketingSchedules(): Promise<any> {
    const response = await api.get('marketing/schedule/');
    return response.data;
  },

  async createMarketingSchedule(scheduleData: any): Promise<any> {
    const response = await api.post('marketing/schedule/create/', scheduleData);
    return response.data;
  },

  async previewSprintLeads(sprintNumber: number): Promise<any> {
    const response = await api.post('marketing/preview-sprint/', { sprint_number: sprintNumber });
    return response.data;
  },
  async getEmailAnalytics(date: string, sprint?: string): Promise<any> {
    const payload: any = { date };
    if (sprint) {
      payload.sprint = sprint;
    }
    const response = await api.post('marketing/email-analytics/', payload);
    return response.data;
  },

  async getSprintsForDate(date: string): Promise<any> {
    const response = await api.post('marketing/sprints-for-date/', { date });
    return response.data;
  },

  async searchSprintNumbers(query: string): Promise<any> {
    const response = await api.get(`marketing/search-sprints/?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  // Admin functions
  async getAllUsers(): Promise<User[]> {
    const response = await api.get('admin/users/');
    return response.data.results || response.data;
  },
};
